import { useQuery } from '@tanstack/react-query';
import { useSourceFileUrl } from '@/hooks/lesson/useSources';

interface UseSourceTextResult {
  text: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Two-stage fetch shared by all text-based viewers (markdown, csv, html,
 * plaintext, docx). First resolves the source's presigned URL via the
 * existing `useSourceFileUrl` hook, then fetches the bytes and decodes
 * them. `expectBinary` returns `null` text and lets the caller pull an
 * `ArrayBuffer` directly via `fetchArrayBuffer` (used by DocxViewer).
 */
export function useSourceText(
  sourceId: string,
  opts: { expectBinary?: boolean } = {}
): UseSourceTextResult {
  const fileUrlQuery = useSourceFileUrl(sourceId);
  const url = fileUrlQuery.data?.url;

  const textQuery = useQuery({
    queryKey: ['source-text', sourceId, url],
    enabled: !!url && !opts.expectBinary,
    queryFn: async () => {
      const res = await fetch(url!);
      if (!res.ok) {
        throw new Error(`Failed to load file (${res.status})`);
      }
      return res.text();
    },
    // Presigned URLs rotate every ~5 min via the file-URL hook; let the
    // text query tag along with the same lifecycle.
    staleTime: 4 * 60 * 1000,
  });

  return {
    text: textQuery.data ?? null,
    isLoading: fileUrlQuery.isLoading || textQuery.isLoading,
    error:
      (fileUrlQuery.error as Error | null) ?? (textQuery.error as Error | null),
  };
}

/**
 * Convenience for binary fetches - used by DocxViewer to pass the
 * ArrayBuffer to docx-preview's `renderAsync`.
 */
export function useSourceArrayBuffer(sourceId: string) {
  const fileUrlQuery = useSourceFileUrl(sourceId);
  const url = fileUrlQuery.data?.url;

  const bufferQuery = useQuery({
    queryKey: ['source-buffer', sourceId, url],
    enabled: !!url,
    queryFn: async () => {
      const res = await fetch(url!);
      if (!res.ok) {
        throw new Error(`Failed to load file (${res.status})`);
      }
      return res.arrayBuffer();
    },
    staleTime: 4 * 60 * 1000,
  });

  return {
    buffer: bufferQuery.data ?? null,
    isLoading: fileUrlQuery.isLoading || bufferQuery.isLoading,
    error:
      (fileUrlQuery.error as Error | null) ??
      (bufferQuery.error as Error | null),
  };
}
